import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/leaflet-setup"; // sets window.L_DISABLE_3D before any leaflet import (foldable GPU fix)
import { initPerfMode } from "@/lib/perfMode";
import App from "@/App";
import { initTheme } from "@/lib/theme";

// Apply the saved theme (incl. "auto" → OS) before render to avoid a flash.
initTheme();
// Foldable/weak-GPU compositor guard: kills backdrop-filter etc. on affected devices.
initPerfMode();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);

// Lift the boot splash once the page has actually finished loading (window
// load = images in) so users never watch content stream in piecemeal — one
// branded splash, one smooth reveal. Hard-capped so it can never trap anyone.
(function liftBootSplash() {
  const boot = document.getElementById("boot-splash");
  if (!boot) return;
  const reveal = () => {
    boot.classList.add("boot-gone");
    setTimeout(() => boot.remove(), 450);
  };
  const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
  const windowLoaded = new Promise((r) => {
    if (document.readyState === "complete") r();
    else window.addEventListener("load", r, { once: true });
  });
  const cap = new Promise((r) => setTimeout(r, 2500));
  Promise.race([Promise.all([fontsReady, windowLoaded]), cap]).then(() =>
    requestAnimationFrame(() => requestAnimationFrame(reveal)),
  );
})();

// Cache the app on the user's device (production builds): JS/CSS/images/fonts
// AND map tiles are served from the device on repeat opens — near-instant loads.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
